import React, { useState, useEffect } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import '../i18n'; // Import i18n configuration
import Sidebar from '../components/driver/Sidebar';
import Header from '../components/driver/Header';
import BottomNav from '../components/driver/BottomNav';
import DriverDashboard from '../components/driver/dashboard/DriverDashboard';
import { Routes, Route } from 'react-router-dom';
import DriverCar from '../components/driver/dashboard/DriverCar';
import AppointmentsMap from '../components/driver/dashboard/AppointmentsMap';
import DriverSettings from '../components/driver/dashboard/DriverSettings';
import DashboardSupport from '../components/driver/dashboard/DriverSupport';
import DriverVerifyKmPage from '../components/driver/dashboard/DriverVerifyKmPage'
import DriverAppointmentsHistory from '../components/driver/dashboard/DriverAppointmentsHistory';
import DriverNotificationsHistory from '../components/driver/dashboard/DriverNotificationsHistory';
import ParkingPayment from '../components/driver/dashboard/ParkingPayment';
import PaymentHistory from '../components/driver/dashboard/PaymentHistory';
import { CarProvider } from '../contexts/CarContext';
import { DriverProvider, useDriver } from '../contexts/DriverContext';

const DriverPageContent = () => {
    const navigate = useNavigate();
    const { driverAccount } = useDriver();

    // Mobile sidebar state
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

    // Mobile navigation style state
    const [mobileNavStyle, setMobileNavStyle] = useState(() => {
        return localStorage.getItem('mobileNavStyle') || 'hamburger';
    });

    const [profileImage, setProfileImage] = useState("");

    // PWA detection
    const [isPWA, setIsPWA] = useState(false);

    useEffect(() => {
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
        setIsPWA(isStandalone);
    }, []);

    // User data derived from driver context
    const userData = {
        name: driverAccount?.name || "",
        email: driverAccount?.email || "",
        phone: driverAccount?.phone || "",
        information_sharing: driverAccount?.information_sharing || false,
        legal_status: driverAccount?.customer_subtype || "UNDEFINED",
        profileImage: profileImage
    };


    useEffect(() => {
        // Load Google Fonts dynamically
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);

        return () => {
            // Clean up
            document.head.removeChild(link);
        };
    }, []);

    // Effect to prevent scrolling when sidebar is open
    useEffect(() => {
        if (sidebarOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [sidebarOpen]);

    // Listen for mobile navigation style changes
    useEffect(() => {
        const handleMobileNavStyleChange = (event) => {
            setMobileNavStyle(event.detail);
        };

        window.addEventListener('mobileNavStyleChange', handleMobileNavStyleChange);

        return () => {
            window.removeEventListener('mobileNavStyleChange', handleMobileNavStyleChange);
        };
    }, []);

    // Handle logout
    const handleLogout = async () => {
        try {
            const auth = getAuth();
            await signOut(auth);
            navigate('/login');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    return (
        <div
            className="flex bg-slate-100 dark:bg-slate-900 font-['Roboto',sans-serif] text-base dark:text-slate-200"
            style={{
                overflow: 'hidden',
            }}
        >
            {/* Overlay for mobile when sidebar is open - improved opacity and z-index */}
            {sidebarOpen && mobileNavStyle === 'hamburger' && (
                <div
                    className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm transition-opacity duration-300 lg:hidden"
                    onClick={toggleSidebar}
                    aria-hidden="true"
                ></div>
            )}

            {/* Sidebar - show on desktop always, on mobile only with hamburger style */}
            <Sidebar
                userData={userData}
                handleLogout={handleLogout}
                isOpen={mobileNavStyle === 'bottom' ? false : sidebarOpen}
                toggleSidebar={toggleSidebar}
                hideOnMobile={mobileNavStyle === 'bottom'}
            />

            {/* Main Content Area - takes remaining space */}
            <div
                className="flex-1 flex flex-col lg:ml-64"
                style={{
                    height: '100dvh',
                    overflow: 'hidden',
                    position: 'relative',
                    paddingBottom: '1rem'
                }}
            >
                {/* Header - fixed at top */}
                <Header
                    toggleSidebar={toggleSidebar}
                    showHamburger={mobileNavStyle === 'hamburger'}
                />

                {/* Scrollable Content - only this area scrolls */}
                <main
                    className="flex-1 px-2 sm:px-4 md:px-2 py-4"
                    style={{
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        WebkitOverflowScrolling: 'touch',
                        overscrollBehavior: 'contain',
                        paddingBottom: mobileNavStyle === 'bottom'
                            ? 'calc(2.5rem + env(safe-area-inset-bottom, 0px))'
                            : 'calc(1rem + env(safe-area-inset-bottom, 10px))',
                        paddingTop: isPWA ? '6.5rem' : '0.5rem'
                    }}
                >
                    <Routes>
                        <Route index element={<DriverDashboard />} />
                        <Route path="cars" element={<DriverCar />} />
                        <Route path="appointments-map" element={<AppointmentsMap />} />
                        <Route path="settings" element={<DriverSettings profileImage={profileImage} handleLogout={handleLogout} />} />
                        <Route path="support" element={<DashboardSupport />} />
                        <Route path="verify-km" element={<DriverVerifyKmPage />} />
                        <Route path="apps" element={<DriverAppointmentsHistory />} />
                        <Route path="notifications" element={<DriverNotificationsHistory />} />
                        <Route path="parking" element={<ParkingPayment />} />
                        <Route path="payment-history" element={<PaymentHistory />} />
                    </Routes>
                </main>
            </div>

            {/* Bottom Navigation - only show on mobile when selected */}
            {mobileNavStyle === 'bottom' && <BottomNav />}
        </div>
    );
};

const DriverPage = () => {
    return (
        <CarProvider>
            <DriverProvider>
                <DriverPageContent />
            </DriverProvider>
        </CarProvider>
    );
};

export default DriverPage;